import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getPortfolioSummary,
  getPortfolioAssessments,
  getPortfolioIncidents,
  // getPortfolioClaims, // Claims hidden - not launching with claims functionality
  // getPortfolioEvents, // Events hidden - not launching with events functionality
  getPortfolioCrossDomain,
  getPortfolioRiskSignals,
  getPortfolioTrends,
  getPortfolioOrganizationsDrilldown,
  getPortfolioProfessionalSportsSummary,
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { ScrollArea } from '../components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  Shield,
  Building2,
  FileText,
  Users,
  DollarSign,
  Calendar as CalendarIcon,
  Activity,
  Target,
  AlertCircle,
  CheckCircle,
  Clock,
  ChevronRight,
  Loader2,
  Lightbulb,
  Eye,
  ArrowUpRight,
  ArrowDownRight,
  PieChart,
  Zap,
  ExternalLink,
  Award,
  AlertOctagon,
  Medal,
} from 'lucide-react';
import { cn } from '../lib/utils';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart as RechartsPie,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import { format } from 'date-fns';

const COLORS = ['#93c5fd', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#6366f1', '#ec4899'];

const SEVERITY_COLORS = {
  Low: 'bg-green-500/20 text-green-600 border-green-500/30',
  Moderate: 'bg-yellow-500/20 text-yellow-600 border-yellow-500/30',
  Elevated: 'bg-orange-500/20 text-orange-600 border-orange-500/30',
  High: 'bg-red-500/20 text-red-600 border-red-500/30',
};

const DIRECTION_ICONS = {
  increasing: TrendingUp,
  decreasing: TrendingDown,
  stable: Minus,
  improving: TrendingDown,
  worsening: TrendingUp,
};

const TIME_PERIODS = [
  { value: '7', label: 'Last 7 Days' },
  { value: '30', label: 'Last 30 Days' },
  { value: '90', label: 'Last 90 Days' },
  { value: '365', label: 'Last Year' },
  { value: 'custom', label: 'Custom Range' },
];

export default function PortfolioDashboard() {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  
  // Data states
  const [summary, setSummary] = useState(null);
  const [assessmentsData, setAssessmentsData] = useState(null);
  const [incidentsData, setIncidentsData] = useState(null);
  // const [claimsData, setClaimsData] = useState(null); // Claims hidden
  // const [eventsData, setEventsData] = useState(null); // Events hidden
  const [crossDomain, setCrossDomain] = useState(null);
  const [riskSignals, setRiskSignals] = useState(null);
  const [trends, setTrends] = useState(null);
  const [proSportsData, setProSportsData] = useState(null);
  
  // Drill-down state
  const [drilldownSheet, setDrilldownSheet] = useState({ open: false, metric: null, title: '', filter: null });
  const [drilldownData, setDrilldownData] = useState(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const navigate = useNavigate();

  // Handle drill-down click
  const handleDrilldown = async (metric, title, filter = null) => {
    setDrilldownSheet({ open: true, metric, title, filter });
    setDrilldownLoading(true);
    setDrilldownData(null);
    
    try {
      const params = { 
        metric, 
        days: period === 'custom' ? 30 : parseInt(period),
        ...(filter && { filter_value: filter })
      };
      const response = await getPortfolioOrganizationsDrilldown(params);
      setDrilldownData(response.data);
    } catch (error) {
      console.error('Error fetching drill-down data:', error);
    } finally {
      setDrilldownLoading(false);
    }
  };

  // Navigate to organization's Risk Overview
  const navigateToOrg = (orgId, orgName) => {
    setDrilldownSheet({ open: false, metric: null, title: '', filter: null });
    // Navigate to Risk Overview (Org IQ) and select the organization
    navigate(`/org-iq?org=${orgId}`);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = period === 'custom' 
        ? { start_date: dateRange.from?.toISOString(), end_date: dateRange.to?.toISOString() }
        : { days: parseInt(period) };

      const [
        summaryRes,
        assessmentsRes,
        incidentsRes,
        // claimsRes, // Claims hidden
        // eventsRes, // Events hidden
        crossDomainRes,
        signalsRes,
        trendsRes,
        proSportsRes,
      ] = await Promise.all([
        getPortfolioSummary(params),
        getPortfolioAssessments(params),
        getPortfolioIncidents(params),
        // getPortfolioClaims(params), // Claims hidden
        // getPortfolioEvents(params), // Events hidden
        getPortfolioCrossDomain(params),
        getPortfolioRiskSignals(params),
        getPortfolioTrends(),
        getPortfolioProfessionalSportsSummary().catch(() => ({ data: null })),
      ]);

      setSummary(summaryRes.data);
      setAssessmentsData(assessmentsRes.data);
      setIncidentsData(incidentsRes.data);
      // setClaimsData(claimsRes.data); // Claims hidden
      // setEventsData(eventsRes.data); // Events hidden
      setCrossDomain(crossDomainRes.data);
      setRiskSignals(signalsRes.data);
      setTrends(trendsRes.data);
      setProSportsData(proSportsRes.data);
    } catch (error) {
      console.error('Error fetching portfolio data:', error);
    } finally {
      setLoading(false);
    }
  }, [period, dateRange]);

  useEffect(() => {
    if (period !== 'custom' || (dateRange.from && dateRange.to)) {
      fetchData();
    }
  }, [fetchData, period, dateRange]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Prepare chart data
  const riskTierData = assessmentsData ? [
    { name: 'Low', value: assessmentsData.risk_tiers.low, color: '#86efac' },
    { name: 'Medium', value: assessmentsData.risk_tiers.medium, color: '#fde047' },
    { name: 'High', value: assessmentsData.risk_tiers.high, color: '#fca5a5' },
    { name: 'Critical', value: assessmentsData.risk_tiers.critical, color: '#f87171' },
  ].filter(d => d.value > 0) : [];

  const severityData = incidentsData ? [
    { name: 'Low', value: incidentsData.severity_distribution.low, color: '#86efac' },
    { name: 'Medium', value: incidentsData.severity_distribution.medium, color: '#fde047' },
    { name: 'High', value: incidentsData.severity_distribution.high, color: '#fca5a5' },
    { name: 'Critical', value: incidentsData.severity_distribution.critical, color: '#f87171' },
  ].filter(d => d.value > 0) : [];

  const trendData = trends?.periods?.map(p => ({
    period: p.period.replace(' (', '\n('),
    incidents: p.incidents,
    assessments: p.assessments,
    // claims: p.claims_count, // Claims hidden
  })).reverse() || [];

  const DirectionIcon = ({ direction, className }) => {
    const Icon = DIRECTION_ICONS[direction] || Minus;
    const colorClass = direction === 'increasing' || direction === 'worsening' 
      ? 'text-red-500' 
      : direction === 'decreasing' || direction === 'improving'
        ? 'text-green-500'
        : 'text-gray-500';
    return <Icon className={cn("w-4 h-4", colorClass, className)} />;
  };

  return (
    <div data-testid="portfolio-dashboard" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold font-['Barlow_Condensed'] uppercase tracking-tight">
            Portfolio Risk Intelligence
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">
            Aggregate visibility across all organizations
          </p>
        </div>
        
        {/* Time Period Selector */}
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[140px] sm:w-[180px]" data-testid="period-selector">
              <CalendarIcon className="w-4 h-4 mr-2 hidden sm:block" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIME_PERIODS.map(p => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {period === 'custom' && (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="min-w-[180px] sm:min-w-[240px] justify-start text-xs sm:text-sm">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {dateRange.from ? (
                    dateRange.to ? (
                      <>
                        {format(dateRange.from, "LLL dd")} - {format(dateRange.to, "LLL dd")}
                      </>
                    ) : format(dateRange.from, "LLL dd, y")
                  ) : (
                    "Pick dates"
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="range"
                  selected={dateRange}
                  onSelect={setDateRange}
                  numberOfMonths={1}
                  className="sm:hidden"
                />
                <Calendar
                  mode="range"
                  selected={dateRange}
                  onSelect={setDateRange}
                  numberOfMonths={2}
                  className="hidden sm:block"
                />
              </PopoverContent>
            </Popover>
          )}
          
          <Button onClick={fetchData} variant="outline" size="icon" className="h-9 w-9 sm:h-10 sm:w-10">
            <Activity className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Executive Summary */}
      <Card className="bg-gradient-to-br from-blue-100 via-blue-50 to-sky-100 dark:from-blue-950/40 dark:via-blue-900/20 dark:to-sky-950/30 border-blue-200 dark:border-blue-500/30">
        <CardHeader className="pb-2 sm:pb-4">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" />
            Executive Portfolio Summary
          </CardTitle>
          <CardDescription className="text-xs sm:text-sm">
            How much risk are we responsible for right now — and where should we act first?
          </CardDescription>
        </CardHeader>
        <CardContent className="p-3 sm:p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6">
            {/* Risk Direction */}
            <div 
              className="space-y-1 sm:space-y-2 p-2 sm:p-3 rounded-lg hover:bg-secondary/30 cursor-pointer transition-colors group"
              onClick={() => handleDrilldown('risk_tiers', 'Organizations by Risk Tier')}
              data-testid="drilldown-risk-direction"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs sm:text-sm text-muted-foreground">Risk Direction</span>
                <ChevronRight className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <div className="flex items-center gap-2">
                <Badge 
                  className={cn(
                    "text-sm sm:text-lg px-2 sm:px-3 py-0.5 sm:py-1",
                    summary?.executive_summary?.risk_direction === 'improving' && 'bg-green-500/20 text-green-600',
                    summary?.executive_summary?.risk_direction === 'stable' && 'bg-yellow-500/20 text-yellow-600',
                    summary?.executive_summary?.risk_direction === 'worsening' && 'bg-red-500/20 text-red-600',
                  )}
                >
                  <DirectionIcon direction={summary?.executive_summary?.risk_direction} className="mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">{summary?.executive_summary?.risk_direction?.charAt(0).toUpperCase() + summary?.executive_summary?.risk_direction?.slice(1)}</span>
                  <span className="sm:hidden">{summary?.executive_summary?.risk_direction?.charAt(0).toUpperCase()}</span>
                </Badge>
              </div>
            </div>
            
            {/* Total Risk Volume */}
            <div 
              className="space-y-1 sm:space-y-2 p-2 sm:p-3 rounded-lg hover:bg-secondary/30 cursor-pointer transition-colors group"
              onClick={() => handleDrilldown('incidents', 'Organizations by Incident Count')}
              data-testid="drilldown-total-incidents"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs sm:text-sm text-muted-foreground">Risk Volume</span>
                <ChevronRight className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <div className="text-2xl sm:text-3xl font-bold">
                {summary?.incidents?.total || 0}
              </div>
              <span className="text-[10px] sm:text-xs text-muted-foreground">total incidents</span>
            </div>
            
            {/* Organizations */}
            <div 
              className="space-y-1 sm:space-y-2 p-2 sm:p-3 rounded-lg hover:bg-secondary/30 cursor-pointer transition-colors group"
              onClick={() => handleDrilldown('organizations', 'All Organizations')}
              data-testid="drilldown-organizations"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs sm:text-sm text-muted-foreground">Organizations</span>
                <ChevronRight className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <div className="text-2xl sm:text-3xl font-bold">{summary?.organizations?.total || 0}</div>
              <div className="flex gap-1 sm:gap-2 flex-wrap">
                <Badge variant="outline" className="text-[10px] sm:text-xs">
                  {summary?.organizations?.sports || 0} Sports
                </Badge>
                <Badge variant="outline" className="text-[10px] sm:text-xs">
                  {summary?.organizations?.fitness || 0} Fitness
                </Badge>
              </div>
            </div>
            
            {/* Open Incidents */}
            <div 
              className="space-y-1 sm:space-y-2 p-2 sm:p-3 rounded-lg hover:bg-secondary/30 cursor-pointer transition-colors group"
              onClick={() => handleDrilldown('open_incidents', 'Organizations with Open Incidents')}
              data-testid="drilldown-open-incidents"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs sm:text-sm text-muted-foreground">Open Incidents</span>
                <ChevronRight className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <div className="text-2xl sm:text-3xl font-bold text-amber-500">
                {summary?.incidents?.open || 0}
              </div>
              <span className="text-[10px] sm:text-xs text-muted-foreground">requiring attention</span>
            </div>
          </div>
          
          {/* Key Signals */}
          {summary?.executive_summary?.key_signals?.length > 0 && (
            <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-border">
              <h4 className="text-xs sm:text-sm font-medium mb-2 sm:mb-3 flex items-center gap-2">
                <Zap className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-500" />
                Key Signals for Leadership
              </h4>
              <ul className="space-y-1.5 sm:space-y-2">
                {summary.executive_summary.key_signals.filter(s => !s.toLowerCase().includes('claim') && !s.toLowerCase().includes('event')).map((signal, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-xs sm:text-sm">
                    <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                    <span>{signal}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Risk Signals Alert */}
      {riskSignals?.signals?.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-500" />
            Portfolio Risk Signals ({riskSignals.signals.filter(s => 
              !s.title?.toLowerCase().includes('claim') && 
              !s.title?.toLowerCase().includes('event') &&
              !s.description?.toLowerCase().includes('claim') &&
              !s.description?.toLowerCase().includes('event')
            ).length})
          </h3>
          <div className="grid grid-cols-1 gap-3">
            {riskSignals.signals
              .filter(signal => 
                !signal.title?.toLowerCase().includes('claim') && 
                !signal.title?.toLowerCase().includes('event') &&
                !signal.description?.toLowerCase().includes('claim') &&
                !signal.description?.toLowerCase().includes('event')
              )
              .map((signal, idx) => (
              <Alert 
                key={idx} 
                className={cn(
                  "border",
                  signal.severity === 'High' && 'border-red-500/50 bg-red-500/5',
                  signal.severity === 'Elevated' && 'border-orange-500/50 bg-orange-500/5',
                  signal.severity === 'Moderate' && 'border-yellow-500/50 bg-yellow-500/5',
                )}
              >
                <AlertCircle className={cn(
                  "w-4 h-4 hidden sm:block",
                  signal.severity === 'High' && 'text-red-500',
                  signal.severity === 'Elevated' && 'text-orange-500',
                  signal.severity === 'Moderate' && 'text-yellow-500',
                )} />
                <AlertTitle className="flex items-start sm:items-center justify-between gap-2 flex-wrap">
                  <span className="text-sm sm:text-base">{signal.title}</span>
                  <Badge className={cn(SEVERITY_COLORS[signal.severity], "text-[10px] sm:text-xs")}>{signal.severity}</Badge>
                </AlertTitle>
                <AlertDescription className="mt-2">
                  <p className="text-xs sm:text-sm">{signal.description}</p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground mt-2">
                    <Lightbulb className="w-3 h-3 inline mr-1" />
                    {signal.recommendation}
                  </p>
                </AlertDescription>
              </Alert>
            ))}
          </div>
        </div>
      )}

      {/* Aggregate Metrics Tabs */}
      <Tabs defaultValue="assessments" className="space-y-3 sm:space-y-4">
        <TabsList className="grid w-full grid-cols-2 h-auto">
          <TabsTrigger value="assessments" data-testid="tab-assessments" className="text-xs sm:text-sm py-2 sm:py-2.5">
            <FileText className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Assessments</span>
            <span className="sm:hidden">Assess</span>
          </TabsTrigger>
          <TabsTrigger value="incidents" data-testid="tab-incidents" className="text-xs sm:text-sm py-2 sm:py-2.5">
            <AlertTriangle className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            Incidents
          </TabsTrigger>
          {/* Claims tab hidden - not launching with claims functionality */}
          {/* Events tab hidden - not launching with events functionality */}
        </TabsList>

        {/* Assessments Tab */}
        <TabsContent value="assessments" className="space-y-3 sm:space-y-4">
          {/* View All Button */}
          <div className="flex justify-end">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/assessments')}
              className="text-xs"
              data-testid="view-all-assessments-btn"
            >
              View All Assessments
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>

          {/* Sports/Fitness Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
            <Card 
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate('/assessments')}
            >
              <CardContent className="pt-4 sm:pt-6 p-3 sm:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Completion Rate</p>
                    <p className="text-2xl font-bold">{assessmentsData?.summary?.completion_rate || 0}%</p>
                  </div>
                  <div className="h-12 w-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-blue-500" />
                  </div>
                </div>
                <Progress value={assessmentsData?.summary?.completion_rate || 0} className="mt-3" />
              </CardContent>
            </Card>
            
            <Card 
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate('/assessments')}
            >
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Average Score</p>
                    <p className="text-2xl font-bold">{assessmentsData?.summary?.average_score || 0}</p>
                  </div>
                  <DirectionIcon direction={summary?.assessments?.trend} className="w-6 h-6" />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Median: {assessmentsData?.summary?.median_score || 0}
                </p>
              </CardContent>
            </Card>
            
            <Card 
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate('/assessments')}
            >
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Assessments</p>
                    <p className="text-2xl font-bold">{assessmentsData?.summary?.total_assessments || 0}</p>
                  </div>
                  <FileText className="w-6 h-6 text-muted-foreground" />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {assessmentsData?.summary?.unique_orgs_assessed || 0} orgs assessed
                </p>
              </CardContent>
            </Card>
            
            <Card 
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate('/assessments')}
            >
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Elevated Risk</p>
                    <p className="text-2xl font-bold text-red-500">
                      {(assessmentsData?.risk_tier_percentages?.high || 0) + (assessmentsData?.risk_tier_percentages?.critical || 0)}%
                    </p>
                  </div>
                  <AlertTriangle className="w-6 h-6 text-red-500" />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  High + Critical tiers
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Professional Sports Summary - Integrated */}
          {proSportsData && proSportsData.total_professional_sports_orgs > 0 && (
            <Card 
              className="bg-gradient-to-br from-purple-100/50 via-purple-50/30 to-indigo-100/50 dark:from-purple-950/30 dark:via-purple-900/10 dark:to-indigo-950/20 border-purple-200/50 dark:border-purple-500/20 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate('/assessments')}
              data-testid="pro-sports-summary-card"
            >
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Medal className="w-4 h-4 text-purple-500" />
                    Professional Sports Assessments
                  </CardTitle>
                  <Button variant="ghost" size="sm" className="text-xs text-purple-600 hover:text-purple-700">
                    View Details <ChevronRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {/* Critical Flags Alert */}
                {proSportsData.critical_flags_summary?.orgs_with_flags > 0 && (
                  <Alert variant="destructive" className="mb-3 py-2 border-red-500/50 bg-red-500/10">
                    <AlertOctagon className="h-3 w-3" />
                    <AlertDescription className="text-xs">
                      <span className="font-bold">{proSportsData.critical_flags_summary.orgs_with_flags}</span> org(s) have 
                      <span className="font-bold"> {proSportsData.critical_flags_summary.total_flags}</span> critical control gaps
                    </AlertDescription>
                  </Alert>
                )}
                
                {/* Summary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <div className="p-2 rounded-lg bg-background/50 text-center">
                    <div className="text-lg font-bold text-purple-600">{proSportsData.assessment_coverage}%</div>
                    <div className="text-[10px] text-muted-foreground">Coverage</div>
                  </div>
                  <div className="p-2 rounded-lg bg-background/50 text-center">
                    <div className="text-lg font-bold">{proSportsData.average_enterprise_score?.toFixed(1) || '--'}</div>
                    <div className="text-[10px] text-muted-foreground">Avg Score</div>
                  </div>
                  <div className="p-2 rounded-lg bg-background/50 text-center">
                    <div className="flex flex-wrap gap-0.5 justify-center">
                      {proSportsData.risk_band_distribution?.Elevated > 0 && (
                        <Badge className="bg-orange-500 text-white text-[9px] px-1">{proSportsData.risk_band_distribution.Elevated} Elev</Badge>
                      )}
                      {proSportsData.risk_band_distribution?.Critical > 0 && (
                        <Badge className="bg-red-500 text-white text-[9px] px-1">{proSportsData.risk_band_distribution.Critical} Crit</Badge>
                      )}
                      {!proSportsData.risk_band_distribution?.Elevated && !proSportsData.risk_band_distribution?.Critical && (
                        <Badge variant="outline" className="text-[9px] px-1">None</Badge>
                      )}
                    </div>
                    <div className="text-[10px] text-muted-foreground">At Risk</div>
                  </div>
                  <div className="p-2 rounded-lg bg-background/50 text-center">
                    <div className={`text-lg font-bold ${proSportsData.critical_flags_summary?.total_flags > 0 ? 'text-red-500' : 'text-green-500'}`}>
                      {proSportsData.critical_flags_summary?.total_flags || 0}
                    </div>
                    <div className="text-[10px] text-muted-foreground">Critical Flags</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Risk Tier Distribution Chart */}
            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/assessments')}>
              <CardHeader>
                <CardTitle className="text-lg">Risk Tier Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPie>
                      <Pie
                        data={riskTierData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {riskTierData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </RechartsPie>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            
            {/* Top Systemic Gaps */}
            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/assessments')}>
              <CardHeader>
                <CardTitle className="text-lg">Top Systemic Gaps</CardTitle>
                <CardDescription>Most common recommendations across portfolio</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {assessmentsData?.top_systemic_gaps?.slice(0, 5).map((gap, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <span className="text-sm truncate flex-1 mr-4">{gap.gap}</span>
                      <Badge variant="secondary">{gap.frequency} orgs</Badge>
                    </div>
                  ))}
                  {(!assessmentsData?.top_systemic_gaps || assessmentsData.top_systemic_gaps.length === 0) && (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      No systemic gaps identified
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Incidents Tab */}
        <TabsContent value="incidents" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Incidents</p>
                    <p className="text-2xl font-bold">{incidentsData?.summary?.total_incidents || 0}</p>
                  </div>
                  <DirectionIcon direction={summary?.incidents?.trend} className="w-6 h-6" />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {incidentsData?.summary?.per_org_average || 0} per org average
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Open Incidents</p>
                    <p className="text-2xl font-bold text-yellow-500">{incidentsData?.summary?.open_incidents || 0}</p>
                  </div>
                  <Clock className="w-6 h-6 text-yellow-500" />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Requiring attention
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Elevated Risk</p>
                    <p className="text-2xl font-bold text-red-500">
                      {incidentsData?.severity_concentration?.elevated_risk || 0}
                    </p>
                  </div>
                  <AlertTriangle className="w-6 h-6 text-red-500" />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {incidentsData?.severity_concentration?.elevated_risk_pct || 0}% of total
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Repeat Offenders</p>
                    <p className="text-2xl font-bold">{incidentsData?.summary?.repeat_incident_orgs || 0}</p>
                  </div>
                  <Users className="w-6 h-6 text-muted-foreground" />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Orgs with 3+ incidents
                </p>
              </CardContent>
            </Card>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Severity Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Severity Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={severityData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                      <XAxis type="number" />
                      <YAxis type="category" dataKey="name" width={60} />
                      <Tooltip />
                      <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                        {severityData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            
            {/* Top Categories */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Top Incident Categories</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {incidentsData?.top_categories?.map((cat, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <span className="text-sm">{cat.category}</span>
                      <Badge variant="outline">{cat.count}</Badge>
                    </div>
                  ))}
                  {(!incidentsData?.top_categories || incidentsData.top_categories.length === 0) && (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      No incidents recorded
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Claims Tab - Hidden: Not launching with claims functionality
        <TabsContent value="claims" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Exposure</p>
                    <p className="text-2xl font-bold text-amber-500">
                      ${(claimsData?.summary?.total_exposure || 0).toLocaleString()}
                    </p>
                  </div>
                  <DollarSign className="w-6 h-6 text-amber-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Claims</p>
                    <p className="text-2xl font-bold">{claimsData?.summary?.total_claims || 0}</p>
                  </div>
                  <FileText className="w-6 h-6 text-muted-foreground" />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {claimsData?.summary?.open_claims || 0} open
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Avg Claim</p>
                    <p className="text-2xl font-bold">
                      ${(claimsData?.summary?.average_claim_amount || 0).toLocaleString()}
                    </p>
                  </div>
                  <BarChart3 className="w-6 h-6 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">High Cost</p>
                    <p className="text-2xl font-bold text-red-500">
                      {claimsData?.risk_concentration?.high_cost_claims || 0}
                    </p>
                  </div>
                  <ArrowUpRight className="w-6 h-6 text-red-500" />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  ≥${(claimsData?.risk_concentration?.high_cost_threshold || 0).toLocaleString()}
                </p>
              </CardContent>
            </Card>
          </div>
          
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Top Loss Drivers</CardTitle>
              <CardDescription>Claim types by total amount</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {claimsData?.top_loss_drivers?.map((driver, idx) => (
                  <div key={idx} className="p-4 bg-secondary/30 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{driver.type}</span>
                      <Badge variant="outline">{driver.count} claims</Badge>
                    </div>
                    <p className="text-2xl font-bold text-amber-500">
                      ${driver.amount.toLocaleString()}
                    </p>
                  </div>
                ))}
                {(!claimsData?.top_loss_drivers || claimsData.top_loss_drivers.length === 0) && (
                  <p className="text-sm text-muted-foreground text-center py-8 col-span-full">
                    No claims recorded
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        */}

        {/* Events Tab - Hidden: Not launching with events functionality
        <TabsContent value="events" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Events</p>
                    <p className="text-2xl font-bold">{eventsData?.summary?.total_events || 0}</p>
                  </div>
                  <CalendarIcon className="w-6 h-6 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">With Incidents</p>
                    <p className="text-2xl font-bold text-yellow-500">
                      {eventsData?.summary?.events_with_incidents || 0}
                    </p>
                  </div>
                  <AlertTriangle className="w-6 h-6 text-yellow-500" />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {eventsData?.summary?.incident_rate || 0}% incident rate
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Attendance</p>
                    <p className="text-2xl font-bold">
                      {(eventsData?.summary?.total_expected_attendance || 0).toLocaleString()}
                    </p>
                  </div>
                  <Users className="w-6 h-6 text-muted-foreground" />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Expected total
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Need Review</p>
                    <p className="text-2xl font-bold">
                      {eventsData?.risk_indicators?.events_needing_review || 0}
                    </p>
                  </div>
                  <Eye className="w-6 h-6 text-muted-foreground" />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Pending AI analysis
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        */}
      </Tabs>

      {/* Cross-Domain Analysis & Trends */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Trends Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Portfolio Trends (90 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                  <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey="incidents" 
                    stackId="1"
                    stroke="#fca5a5" 
                    fill="#fca5a5" 
                    fillOpacity={0.3}
                    name="Incidents"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="assessments" 
                    stackId="2"
                    stroke="#3b82f6" 
                    fill="#3b82f6" 
                    fillOpacity={0.3}
                    name="Assessments"
                  />
                  {/* Claims line hidden - not launching with claims functionality */}
                </AreaChart>
              </ResponsiveContainer>
            </div>
            
            {trends?.directions && (
              <div className="mt-4 flex gap-4 justify-center">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">Incidents:</span>
                  <DirectionIcon direction={trends.directions.incidents} />
                  <span className="capitalize">{trends.directions.incidents}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">Assessments:</span>
                  <DirectionIcon direction={trends.directions.assessments} />
                  <span className="capitalize">{trends.directions.assessments}</span>
                </div>
                {/* Claims direction hidden */}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Cross-Domain Patterns */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5" />
              Cross-Domain Pattern Analysis
            </CardTitle>
            <CardDescription>Relationships between assessment scores and incidents</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {crossDomain?.patterns
                ?.filter(pattern => !pattern.pattern?.toLowerCase().includes('claim'))
                ?.map((pattern, idx) => (
                <div key={idx} className="p-4 bg-secondary/30 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">{pattern.pattern}</span>
                    <Badge className={SEVERITY_COLORS[pattern.severity]}>{pattern.severity}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">{pattern.finding}</p>
                </div>
              ))}
              
              {crossDomain?.correlations && (
                <div className="grid grid-cols-1 gap-4 mt-4">
                  <div className="p-4 bg-red-500/10 rounded-lg border border-red-500/30">
                    <p className="text-sm text-muted-foreground">Low Score + High Incidents</p>
                    <p className="text-2xl font-bold text-red-500">
                      {crossDomain.correlations.low_score_high_incidents?.count || 0}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">organizations at risk</p>
                  </div>
                  {/* High Incidents + Claims card hidden - not launching with claims functionality
                  <div className="p-4 bg-orange-500/10 rounded-lg border border-orange-500/30">
                    <p className="text-sm text-muted-foreground">High Incidents + Claims</p>
                    <p className="text-2xl font-bold text-orange-500">
                      {crossDomain.correlations.high_incidents_high_claims?.count || 0}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">organizations with claims</p>
                  </div>
                  */}
                </div>
              )}
              
              {/* Drill-Down Opportunities */}
              <div className="mt-4 pt-4 border-t border-border">
                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  Drill-Down Opportunities
                </h4>
                <ul className="space-y-1">
                  {crossDomain?.drill_down_opportunities
                    ?.filter(opp => !opp.toLowerCase().includes('claim') && !opp.toLowerCase().includes('event'))
                    ?.map((opportunity, idx) => (
                    <li key={idx} className="text-sm text-muted-foreground flex items-center gap-2">
                      <ChevronRight className="w-4 h-4" />
                      {opportunity}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Strategic Recommendations */}
      {riskSignals?.recommendations && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-yellow-500" />
              Strategic Recommendations
            </CardTitle>
            <CardDescription>Platform-level actions based on portfolio analysis</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {riskSignals.recommendations.immediate_actions?.length > 0 && (
                <div>
                  <h4 className="font-medium text-red-500 mb-3 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Immediate Actions
                  </h4>
                  <ul className="space-y-2">
                    {riskSignals.recommendations.immediate_actions
                      .filter(a => !a.toLowerCase().includes('claim') && !a.toLowerCase().includes('event'))
                      .map((action, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <ChevronRight className="w-4 h-4 mt-0.5 text-red-500" />
                        {action}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {riskSignals.recommendations.near_term_actions?.length > 0 && (
                <div>
                  <h4 className="font-medium text-yellow-500 mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Near-Term Actions
                  </h4>
                  <ul className="space-y-2">
                    {riskSignals.recommendations.near_term_actions
                      .filter(a => !a.toLowerCase().includes('claim') && !a.toLowerCase().includes('event'))
                      .map((action, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <ChevronRight className="w-4 h-4 mt-0.5 text-yellow-500" />
                        {action}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {(!riskSignals.recommendations.immediate_actions?.length && !riskSignals.recommendations.near_term_actions?.length) && (
                <div className="col-span-full text-center py-8">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  <p className="text-muted-foreground">No immediate actions required. Portfolio is in good standing.</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Drill-Down Sheet */}
      <Sheet open={drilldownSheet.open} onOpenChange={(open) => setDrilldownSheet({ ...drilldownSheet, open })}>
        <SheetContent className="w-full sm:max-w-xl md:max-w-2xl overflow-hidden">
          <SheetHeader className="pb-4">
            <SheetTitle className="flex items-center gap-2 text-lg font-['Barlow_Condensed'] uppercase tracking-tight">
              <Building2 className="w-5 h-5" />
              {drilldownSheet.title}
            </SheetTitle>
            <SheetDescription>
              Click on an organization to view its detailed Risk Overview
            </SheetDescription>
          </SheetHeader>
          
          {drilldownLoading ? (
            <div className="flex items-center justify-center h-[400px]">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : drilldownData?.organizations?.length > 0 ? (
            <ScrollArea className="h-[calc(100vh-180px)]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40%]">Organization</TableHead>
                    {drilldownSheet.metric === 'incidents' && (
                      <>
                        <TableHead className="text-center">Incidents</TableHead>
                        <TableHead className="text-center hidden sm:table-cell">Critical/High</TableHead>
                        <TableHead className="text-center hidden sm:table-cell">Open</TableHead>
                      </>
                    )}
                    {drilldownSheet.metric === 'open_incidents' && (
                      <>
                        <TableHead className="text-center">Open</TableHead>
                        <TableHead className="text-center hidden sm:table-cell">Critical/High</TableHead>
                      </>
                    )}
                    {drilldownSheet.metric === 'assessments' && (
                      <>
                        <TableHead className="text-center">Score</TableHead>
                        <TableHead className="text-center">Risk Level</TableHead>
                      </>
                    )}
                    {drilldownSheet.metric === 'risk_tiers' && (
                      <>
                        <TableHead className="text-center">Risk Level</TableHead>
                        <TableHead className="text-center">Score</TableHead>
                      </>
                    )}
                    {drilldownSheet.metric === 'organizations' && (
                      <>
                        <TableHead className="text-center">Score</TableHead>
                        <TableHead className="text-center hidden sm:table-cell">Incidents</TableHead>
                        <TableHead className="text-center hidden sm:table-cell">Open</TableHead>
                      </>
                    )}
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {drilldownData.organizations.map((org) => (
                    <TableRow 
                      key={org.organization_id} 
                      className="cursor-pointer hover:bg-secondary/50"
                      onClick={() => navigateToOrg(org.organization_id, org.organization_name)}
                    >
                      <TableCell>
                        <div className="space-y-1">
                          <p className="font-medium text-sm truncate max-w-[200px]">{org.organization_name}</p>
                          <div className="flex items-center gap-1.5">
                            <Badge variant="outline" className="text-[10px] capitalize">{org.level}</Badge>
                            <Badge variant="outline" className="text-[10px]">{org.org_type}</Badge>
                          </div>
                        </div>
                      </TableCell>
                      
                      {drilldownSheet.metric === 'incidents' && (
                        <>
                          <TableCell className="text-center">
                            <span className="font-bold">{org.count}</span>
                          </TableCell>
                          <TableCell className="text-center hidden sm:table-cell">
                            <Badge className={cn(
                              "text-xs",
                              (org.details?.critical + org.details?.high) > 0 
                                ? "bg-red-500/20 text-red-500" 
                                : "bg-secondary text-muted-foreground"
                            )}>
                              {(org.details?.critical || 0) + (org.details?.high || 0)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center hidden sm:table-cell">
                            <Badge className={cn(
                              "text-xs",
                              org.details?.open > 0 
                                ? "bg-amber-500/20 text-amber-500" 
                                : "bg-secondary text-muted-foreground"
                            )}>
                              {org.details?.open || 0}
                            </Badge>
                          </TableCell>
                        </>
                      )}
                      
                      {drilldownSheet.metric === 'open_incidents' && (
                        <>
                          <TableCell className="text-center">
                            <Badge className="bg-amber-500/20 text-amber-500 text-xs">
                              {org.count}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center hidden sm:table-cell">
                            <Badge className={cn(
                              "text-xs",
                              (org.details?.by_severity?.critical + org.details?.by_severity?.high) > 0 
                                ? "bg-red-500/20 text-red-500" 
                                : "bg-secondary text-muted-foreground"
                            )}>
                              {(org.details?.by_severity?.critical || 0) + (org.details?.by_severity?.high || 0)}
                            </Badge>
                          </TableCell>
                        </>
                      )}
                      
                      {drilldownSheet.metric === 'assessments' && (
                        <>
                          <TableCell className="text-center">
                            <span className={cn(
                              "font-bold",
                              org.score >= 80 && "text-green-500",
                              org.score >= 60 && org.score < 80 && "text-yellow-500",
                              org.score < 60 && "text-red-500"
                            )}>
                              {org.score}%
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge className={cn(
                              "text-xs capitalize",
                              org.risk_level === 'low' && "bg-green-500/20 text-green-500",
                              org.risk_level === 'medium' && "bg-yellow-500/20 text-yellow-500",
                              org.risk_level === 'high' && "bg-orange-500/20 text-orange-500",
                              org.risk_level === 'critical' && "bg-red-500/20 text-red-500",
                            )}>
                              {org.risk_level}
                            </Badge>
                          </TableCell>
                        </>
                      )}
                      
                      {drilldownSheet.metric === 'risk_tiers' && (
                        <>
                          <TableCell className="text-center">
                            <Badge className={cn(
                              "text-xs capitalize",
                              org.risk_level === 'low' && "bg-green-500/20 text-green-500",
                              org.risk_level === 'medium' && "bg-yellow-500/20 text-yellow-500",
                              org.risk_level === 'high' && "bg-orange-500/20 text-orange-500",
                              org.risk_level === 'critical' && "bg-red-500/20 text-red-500",
                            )}>
                              {org.risk_level}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="text-sm">{org.score}%</span>
                          </TableCell>
                        </>
                      )}
                      
                      {drilldownSheet.metric === 'organizations' && (
                        <>
                          <TableCell className="text-center">
                            {org.assessment_score !== null ? (
                              <span className={cn(
                                "font-medium",
                                org.assessment_score >= 80 && "text-green-500",
                                org.assessment_score >= 60 && org.assessment_score < 80 && "text-yellow-500",
                                org.assessment_score < 60 && "text-red-500"
                              )}>
                                {org.assessment_score}%
                              </span>
                            ) : (
                              <span className="text-muted-foreground text-xs">N/A</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center hidden sm:table-cell">
                            {org.incident_count > 0 ? (
                              <span className="font-medium">{org.incident_count}</span>
                            ) : (
                              <span className="text-muted-foreground">0</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center hidden sm:table-cell">
                            {org.open_incidents > 0 ? (
                              <Badge className="bg-amber-500/20 text-amber-500 text-xs">{org.open_incidents}</Badge>
                            ) : (
                              <span className="text-muted-foreground">0</span>
                            )}
                          </TableCell>
                        </>
                      )}
                      
                      <TableCell>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          ) : (
            <div className="flex flex-col items-center justify-center h-[300px] text-muted-foreground">
              <Building2 className="w-12 h-12 mb-3 opacity-50" />
              <p>No organizations found for this metric</p>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
